import React, {useState} from 'react';

import {Button, EditIcon} from '@kit';

import {wordByCount} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import type {SpellcastingSummary} from '../model/character';
import {abilityLabel} from '../model/labels';
import {spellEntries} from '../model/action-list';
import {spellManagementTabs, spellPreparations, SpellPreparation} from '../model/spell-list';
import type {SpellSlot} from '../model/spell-slots';
import {ActionTable, ActionTableGroup} from './action-table';
import {RollButton} from './dice-roller';
import {FilterChip, FilterChips} from './filter-chips';
import {formatBonus} from './format';
import {Marks} from './marks';
import {SpellManagement} from './spell-management';
import {useWideScreen} from './use-wide-screen';

import css from './sheet.module.css';

const ALL_LEVELS = 'all';

export const freePreparationCount = (preparations: SpellPreparation[]) =>
	preparations.reduce(
		(total, {choice, limit, candidates}) =>
			total +
			(limit === undefined ? 0 : Math.max(0, Math.min(limit, candidates.length) - choice.selected.length)),
		0,
	);

export const SpellsTab: React.FC = () => {
	const {catalog, character} = useCharacter();
	const [isManaging, setManaging] = useState(false);
	const [levelFilter, setLevelFilter] = useState<string>(ALL_LEVELS);
	const isWide = useWideScreen();

	if (!character.canCastSpells) return <p className={css.muted}>Персонаж не владеет заклинаниями.</p>;

	const {spellcasting, spellSlots} = character;
	const preparations = spellPreparations(catalog, character);
	const castingEntries = spellEntries(catalog, character).filter((entry) => entry.spellCasting);
	const freePreparations = freePreparationCount(preparations);
	const levelOf = (spellId: string) => catalog.getSpell(spellId)?.level ?? 0;

	const slotLevels = spellSlots.map((slot) => slot.level);
	const levels = [...new Set([...castingEntries.map((entry) => levelOf(entry.spellId!)), ...slotLevels])].sort(
		(first, second) => first - second,
	);
	const chips: FilterChip<string>[] = [
		{value: ALL_LEVELS, label: 'Все'},
		...levels.map((level) => ({value: String(level), label: level === 0 ? 'Фокусы' : `${level} круг`})),
	];
	const groups: ActionTableGroup[] = levels
		.filter((level) => levelFilter === ALL_LEVELS || String(level) === levelFilter)
		.map((level) => ({
			key: String(level),
			title: level === 0 ? 'Фокусы' : `${level} круг`,
			aside: <LevelSlots slots={spellSlots.filter((slot) => slot.level === level)} />,
			entries: castingEntries.filter((entry) => levelOf(entry.spellId!) === level),
		}));

	return (
		<>
			<div className={css.spellcastingStats}>
				{spellcasting.map((source, index) => (
					<SpellcastingStats key={`${source.classId ?? source.title}-${index}`} source={source} />
				))}
			</div>

			<div className={css.tabToolbar}>
				<FilterChips label="Круг заклинаний" items={chips} value={levelFilter} onChange={setLevelFilter} />
				<div className={css.spellManagement}>
					{freePreparations > 0 && (
						<span className={css.attentionText}>
							Можно подготовить ещё{' '}
							{wordByCount(freePreparations, ['заклинание', 'заклинания', 'заклинаний'], true)}
						</span>
					)}
					<Button
						appearance={freePreparations > 0 ? 'primary' : 'secondary'}
						icon={<EditIcon />}
						title={isWide ? undefined : 'Управление заклинаниями'}
						aria-label={isWide ? undefined : 'Управление заклинаниями'}
						onClick={() => setManaging(true)}
					>
						{isWide && 'Управление заклинаниями'}
					</Button>
				</div>
			</div>

			<ActionTable groups={groups} withCastingTime />

			<SpellManagement
				open={isManaging}
				tabs={spellManagementTabs(preparations, character)}
				onClose={() => setManaging(false)}
			/>
		</>
	);
};

const LevelSlots: React.FC<{slots: SpellSlot[]}> = ({slots}) => {
	const {update} = useCharacter();

	const setAvailableSlots = (slot: SpellSlot, available: number) =>
		update((current) => ({
			spentSpellSlots: {
				...current.spentSpellSlots,
				[slot.key]: slot.max - Math.max(0, Math.min(Math.max(slot.max, slot.max - slot.spent), available)),
			},
		}));

	return (
		<span className={css.levelSlots}>
			{slots.map((slot) => (
				<span key={slot.key} className={css.levelSlot}>
					<span className={css.slotTitle}>{slot.pact ? 'Ячейки договора' : 'Ячейки'}</span>
					<Marks
						shape="square"
						total={Math.max(slot.max, slot.max - slot.spent)}
						filled={slot.max - slot.spent}
						label={`Ячейки ${slot.level} круга`}
						onChange={(available) => setAvailableSlots(slot, available)}
					/>
				</span>
			))}
		</span>
	);
};

const SpellcastingStats: React.FC<{source: SpellcastingSummary}> = ({source}) => {
	const {catalog, character} = useCharacter();
	const title = (source.classId && catalog.getClass(source.classId)?.title) || source.title;
	const modifier = source.ability ? character.abilityModifier(source.ability) : undefined;

	return (
		<section className={css.spellcastingSource}>
			<h3 className={css.groupTitle}>
				{title}
				{source.ability && <span className={css.muted}> · {abilityLabel(source.ability)}</span>}
			</h3>
			<dl className={css.stats}>
				<div className={css.stat}>
					<dt className={css.statLabel}>Модификатор</dt>
					<dd className={css.statValue}>{modifier != null ? formatBonus(modifier) : '—'}</dd>
				</div>
				<div className={css.stat}>
					<dt className={css.statLabel}>Бонус атаки</dt>
					<dd className={css.statValue}>
						{source.attackBonus != null ? (
							<RollButton
								title={`${title}: атака заклинанием`}
								bonus={source.attackBonus}
								context={{kind: 'attack'}}
								className={css.statRoll}
							>
								{formatBonus(source.attackBonus)}
							</RollButton>
						) : (
							'—'
						)}
					</dd>
				</div>
				<div className={css.stat}>
					<dt className={css.statLabel}>СЛ испытания</dt>
					<dd className={css.statValue}>{source.saveDifficulty ?? '—'}</dd>
				</div>
			</dl>
		</section>
	);
};
